import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, Sparkles, ExternalLink, Upload, Image as ImageIcon, Send, MessageSquare, Bell, FileText, Bot, BookmarkPlus, ListChecks, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // AI chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [aiThinking, setAiThinking] = useState(false);
  const [savingDecisionId, setSavingDecisionId] = useState("");
  const chatEndRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projekt/${token}`);
      setData(r.data);
      setAuthorName(prev => prev || r.data.customer?.contact_name || "");
      setDecisions(r.data.decisions || []);
    } catch {
      setError("Projektet kunde inte hittas");
    }
  }, [token]);

  const fetchChat = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projekt/${token}/chat`);
      setChatMessages(r.data.messages || []);
      setDecisions(r.data.decisions || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    Promise.all([refresh(), fetchChat()]).finally(() => setLoading(false));
  }, [refresh, fetchChat]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, aiThinking]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/projekt/${token}/feedback`, { text: feedbackText.trim(), author_name: authorName.trim() });
      setFeedbackText("");
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte skicka");
    }
    setSubmitting(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/projekt/${token}/upload`, fd);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Uppladdning misslyckades");
    }
    setUploading(false);
  };

  const [deletingFileId, setDeletingFileId] = useState("");
  const handleDeleteFile = async (f) => {
    if (!window.confirm(`Vill du verkligen radera "${f.filename}"?`)) return;
    setDeletingFileId(f.id);
    try {
      await axios.delete(`${API}/projekt/${token}/files/${f.id}`);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte radera filen");
    }
    setDeletingFileId("");
  };

  const sendChat = async (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || aiThinking) return;
    setChatInput("");
    setAiThinking(true);
    // Optimistic user message
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", text, created_at: new Date().toISOString() };
    setChatMessages(prev => [...prev, tempUser]);
    try {
      const r = await axios.post(`${API}/projekt/${token}/chat`, { text });
      setChatMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUser.id);
        return [...withoutTemp, r.data.user_message, r.data.ai_message];
      });
    } catch (err) {
      setChatMessages(prev => prev.filter(m => m.id !== tempUser.id));
      alert(err.response?.data?.detail || "AI svarade inte. Försök igen.");
      setChatInput(text);
    }
    setAiThinking(false);
  };

  const saveAsDecision = async (msg) => {
    setSavingDecisionId(msg.id);
    try {
      const r = await axios.post(`${API}/projekt/${token}/decisions`, {
        text: msg.text,
        source_message_id: msg.id,
        saved_by: authorName.trim(),
      });
      setDecisions(prev => [...prev, r.data]);
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte spara");
    }
    setSavingDecisionId("");
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#0A8294] animate-spin" /></div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-[#F59073] mx-auto mb-3" />
          <p className="text-[#0A1628] text-lg mb-1">{error || "Projektet hittades inte"}</p>
          <p className="text-[#64748B] text-sm">Kontrollera länken eller kontakta oss på hey@wedowedo.co</p>
        </div>
      </div>
    );
  }

  const isImage = (f) => (f.content_type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|svg)$/i.test(f.filename || "");

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A1628] relative overflow-hidden" data-testid="project-portal">
      {/* Ambient bg */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#0A8294]/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 -right-24 w-[420px] h-[420px] bg-[#F59073]/15 rounded-full blur-[100px]" />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, #0A1628 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm border border-[#0A8294]/20 shadow-sm">
            <Sparkles className="w-3 h-3 text-[#0A8294]" />
            <span className="text-[10px] font-mono text-[#0A8294] tracking-[0.2em] uppercase">PROJEKTPORTAL</span>
          </div>
          <h1 className="font-outfit font-bold text-3xl sm:text-5xl mb-3 leading-[1.1] tracking-tight">{data.title || data.name}</h1>
          {data.description && <p className="text-[#475569] text-base leading-relaxed">{data.description}</p>}
          {data.customer?.company_name && <p className="text-[#94A3B8] text-sm mt-2">För {data.customer.company_name}</p>}
        </div>

        {/* Live preview */}
        {data.preview_url && (
          <a href={data.preview_url} target="_blank" rel="noopener noreferrer" data-testid="preview-link"
            className="flex items-center justify-between bg-white/80 backdrop-blur-sm border border-[#E5E7EB] hover:border-[#0A8294]/40 hover:shadow-md rounded-xl p-5 mb-10 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0A8294]/15 to-[#0A8294]/5 border border-[#0A8294]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#0A8294]" />
              </div>
              <div>
                <p className="text-[#0A1628] text-sm font-semibold">Live preview</p>
                <p className="text-[#64748B] text-xs mt-0.5">{data.preview_url}</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0A8294] transition-colors" />
          </a>
        )}

        {/* Updates from us */}
        {data.updates && data.updates.length > 0 && (
          <Card label="LÖPANDE UPPDATERINGAR FRÅN OSS" icon={Bell}>
            <div className="space-y-4">
              {data.updates.map(u => (
                <div key={u.id} className="border-l-4 border-[#0A8294] pl-4 py-1" data-testid={`update-${u.id}`}>
                  {u.title && <p className="text-[#0A1628] text-sm font-semibold mb-1">{u.title}</p>}
                  {u.text && <p className="text-[#334155] text-sm leading-relaxed whitespace-pre-line">{u.text}</p>}
                  {u.preview_url && (
                    <a href={u.preview_url} target="_blank" rel="noopener noreferrer" className="text-[#0A8294] text-xs hover:underline mt-1 inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Ny preview-länk
                    </a>
                  )}
                  <p className="text-[#94A3B8] text-[10px] font-mono mt-1">{(u.created_at || "").slice(0, 16).replace("T", " ")} &middot; {u.author || "WeDoWeDo"}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Customer feedback / ideas */}
        <Card label="DINA IDÉER, ÄNDRINGAR & FEEDBACK" icon={MessageSquare} testId="feedback-section">
          <p className="text-[#475569] text-sm mb-4 leading-relaxed">Skriv fritt om vad du vill — idéer, ändringar, frågor. Vi läser allt och svarar via uppdateringarna ovan.</p>
          <form onSubmit={submitFeedback} className="space-y-3">
            <input value={authorName} onChange={e => setAuthorName(e.target.value)}
              placeholder="Ditt namn (valfritt)"
              data-testid="author-name"
              className="w-full bg-white border border-[#E5E7EB] text-[#0A1628] px-3 py-2 rounded-sm text-sm focus:border-[#0A8294] outline-none" />
            <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} required rows={5}
              placeholder="T.ex. 'Kan vi byta ut hjältebilden mot något lugnare? Och göra primärfärgen lite varmare?'"
              data-testid="feedback-text"
              className="w-full bg-white border border-[#E5E7EB] text-[#0A1628] px-3 py-3 rounded-sm text-sm focus:border-[#0A8294] outline-none resize-none leading-relaxed" />
            <button type="submit" disabled={submitting || !feedbackText.trim()} data-testid="submit-feedback"
              className="inline-flex items-center gap-2 bg-[#0A8294] text-white text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-[#086d7d] hover:shadow-[0_8px_24px_-8px_rgba(10,130,148,0.5)] transition-all disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Skickar…" : "Skicka"}
            </button>
          </form>

          {data.feedback && data.feedback.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E5E7EB] space-y-3">
              <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.15em]">KONVERSATION ({data.feedback.length})</p>
              {data.feedback.map(f => {
                const isAdmin = f.role === "admin";
                return (
                  <div key={f.id} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${isAdmin ? "bg-white border border-[#0A8294]/30 text-[#0A1628] rounded-bl-sm" : "bg-[#0A8294]/10 border border-[#0A8294]/20 text-[#0A1628] rounded-br-sm"}`} data-testid={`feedback-${f.id}`}>
                      {isAdmin && <p className="text-[10px] font-mono text-[#0A8294] tracking-[0.15em] font-semibold mb-1.5">WEDOWEDO</p>}
                      {!isAdmin && <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.15em] mb-1.5">{f.author_name || "DU"}</p>}
                      <p className="text-sm leading-relaxed whitespace-pre-line">{f.text}</p>
                      <p className="text-[#94A3B8] text-[10px] font-mono mt-1.5">{(f.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* AI bollplank */}
        <Card label="AI-BOLLPLANK · BRAINSTORMA & SPECIFICERA" icon={Bot} testId="ai-chat-section">
          <p className="text-[#475569] text-sm mb-4 leading-relaxed">Bolla idéer, funktioner och innehåll med wedowedos AI-strateg. När ni landar i ett beslut — klicka <span className="font-medium text-[#0A8294]">"Spara som beslut"</span> så fastnar det i specifikationen.</p>

          {chatMessages.length === 0 && !aiThinking && (
            <div className="bg-[#0A8294]/5 border border-[#0A8294]/20 rounded-lg p-4 mb-4">
              <p className="text-[#0A1628] text-sm font-medium mb-1">Förslag att börja med</p>
              <ul className="text-[#475569] text-sm space-y-1 list-disc list-inside">
                <li>"Vilka sektioner bör startsidan ha för att konvertera B2B-besökare?"</li>
                <li>"Hjälp mig formulera en stark hero-rubrik som speglar vår positionering."</li>
                <li>"Vad behöver ni för material från oss för att komma igång?"</li>
              </ul>
            </div>
          )}

          {chatMessages.length > 0 && (
            <div className="space-y-3 mb-4 max-h-[480px] overflow-y-auto pr-1" data-testid="chat-messages">
              {chatMessages.map(m => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user"
                    ? "bg-[#0A8294] text-white rounded-br-sm"
                    : "bg-white border border-[#E5E7EB] text-[#0A1628] rounded-bl-sm"}`}
                    data-testid={`chat-msg-${m.role}`}>
                    {m.role === "assistant" && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Bot className="w-3 h-3 text-[#0A8294]" />
                        <span className="text-[10px] font-mono text-[#94A3B8] tracking-[0.15em]">WEDOWEDO AI</span>
                      </div>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-line">{m.text}</p>
                    {m.role === "assistant" && !String(m.id).startsWith("tmp-") && (
                      <button onClick={() => saveAsDecision(m)} disabled={savingDecisionId === m.id || decisions.some(d => d.source_message_id === m.id)}
                        data-testid={`save-decision-${m.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0A8294] hover:text-[#086d7d] disabled:text-[#94A3B8] transition-colors">
                        {savingDecisionId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3" />}
                        {decisions.some(d => d.source_message_id === m.id) ? "Sparat som beslut" : "Spara som beslut"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {aiThinking && (
                <div className="flex justify-start" data-testid="ai-thinking">
                  <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 text-[#0A8294] animate-spin" />
                    <span className="text-xs text-[#64748B]">AI tänker…</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}

          <form onSubmit={sendChat} className="flex gap-2 items-end">
            <textarea value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(e); } }}
              rows={2} disabled={aiThinking}
              placeholder="Fråga AI:n om funktioner, sektioner, copy, integrationer…"
              data-testid="chat-input"
              className="flex-1 bg-white border border-[#E5E7EB] text-[#0A1628] px-3 py-2.5 rounded-sm text-sm focus:border-[#0A8294] outline-none resize-none leading-relaxed disabled:opacity-60" />
            <button type="submit" disabled={aiThinking || !chatInput.trim()} data-testid="chat-send"
              className="inline-flex items-center justify-center bg-[#0A8294] text-white w-11 h-11 rounded-sm hover:bg-[#086d7d] hover:shadow-[0_8px_24px_-8px_rgba(10,130,148,0.5)] transition-all disabled:opacity-50">
              {aiThinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </Card>

        {/* Decisions / spec */}
        {decisions.length > 0 && (
          <Card label={`BESLUT & SPECIFIKATION · ${decisions.length}`} icon={ListChecks} testId="decisions-section">
            <p className="text-[#475569] text-sm mb-4 leading-relaxed">Beslut ni tagit i AI-bollplanket. Detta blir grunden för vår leverans.</p>
            <div className="space-y-2">
              {decisions.map((d, i) => (
                <div key={d.id} className="bg-[#0A8294]/5 border border-[#0A8294]/20 rounded-lg p-3 flex items-start gap-3" data-testid={`decision-${d.id}`}>
                  <span className="text-[#0A8294] font-mono text-xs font-bold mt-0.5 shrink-0">#{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {d.by_admin && <span className="font-mono text-[9px] bg-[#F59073]/15 text-[#0A8294] border border-[#0A8294]/30 px-1.5 py-0.5 rounded-sm tracking-wider">FRÅN WEDOWEDO</span>}
                    </div>
                    <p className="text-[#0A1628] text-sm leading-relaxed whitespace-pre-line">{d.text}</p>
                    <p className="text-[#94A3B8] text-[10px] font-mono mt-1.5">{d.saved_by || "Du"} &middot; {(d.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Files */}
        <Card label="FILER · LOGOTYPER & BILDER" icon={ImageIcon} testId="files-section">
          <p className="text-[#475569] text-sm mb-4 leading-relaxed">Ladda upp logotyp, bilder, mockups eller allt material vi ska använda. Max 50 MB per fil.</p>
          <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-8 cursor-pointer transition-colors ${uploading ? "border-[#94A3B8] text-[#94A3B8]" : "border-[#0A8294]/30 text-[#0A8294] hover:border-[#0A8294]/60 hover:bg-[#0A8294]/5"}`} data-testid="upload-area">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            <span className="text-sm font-medium">{uploading ? "Laddar upp…" : "Ladda upp fil eller bild"}</span>
            <span className="text-[10px] text-[#94A3B8]">Eller dra och släpp filen här</span>
            <input type="file" disabled={uploading} className="hidden"
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUpload(f); }} />
          </label>

          {data.files && data.files.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...data.files].reverse().map(f => (
                <div key={f.id} className="group relative bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:border-[#0A8294]/40 hover:shadow-md transition-all" data-testid={`file-${f.id}`}>
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="block">
                    {isImage(f) ? (
                      <img src={f.url} alt={f.filename} className="w-full aspect-square object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-square bg-[#F8FAFC] flex items-center justify-center">
                        <FileText className="w-8 h-8 text-[#94A3B8]" />
                      </div>
                    )}
                    <div className="p-2 pr-8">
                      <p className="text-[#0A1628] text-xs font-medium truncate">{f.filename}</p>
                      <p className="text-[#94A3B8] text-[10px]">{((f.size || 0) / 1024).toFixed(0)} KB</p>
                    </div>
                  </a>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteFile(f); }}
                    disabled={deletingFileId === f.id}
                    title="Radera fil"
                    data-testid={`delete-file-${f.id}`}
                    className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-sm border border-[#E5E7EB] rounded-md p-1.5 text-[#94A3B8] opacity-0 group-hover:opacity-100 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all disabled:opacity-100 disabled:cursor-wait shadow-sm"
                  >
                    {deletingFileId === f.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <footer className="mt-12 pt-6 border-t border-[#E5E7EB] text-[11px] text-[#94A3B8] text-center">
          WeDoWeDo OÜ &middot; <a href="mailto:hey@wedowedo.co" className="text-[#475569] hover:text-[#0A8294]">hey@wedowedo.co</a>
        </footer>
      </div>
    </div>
  );
}

function Card({ label, icon: Icon, children, testId }) {
  return (
    <section className="bg-white/80 backdrop-blur-sm border border-[#E5E7EB] rounded-2xl p-7 mb-10 shadow-[0_8px_32px_-16px_rgba(10,22,40,0.08)]" data-testid={testId}>
      <div className="flex items-center gap-2 mb-5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />}
        <p className="text-[10px] font-mono text-[#94A3B8] tracking-[0.2em]">{label}</p>
      </div>
      {children}
    </section>
  );
}
